import { useMemo } from 'react';
import { DashboardLayoutConfig } from '../contracts/layout.contract';
import { DashboardGrid, GridItem } from './DashboardGrid';
import { widgetRegistry } from '../registry/WidgetRegistry';
import { useRole } from '@/shared/contexts/RoleContext';
import { useUser } from '@/shared/hooks/useAuth';

export interface DashboardLayoutEngineProps {
  layout: DashboardLayoutConfig;
}

export function DashboardLayoutEngine({ layout }: DashboardLayoutEngineProps) {
  const { can } = useRole();
  const user = useUser();

  // Filter widgets that are correctly registered and where the user has permission
  const activeWidgets = useMemo(() => {
    return layout.widgets
      .map(pos => {
        const registered = widgetRegistry.getWidget(pos.id);
        return { pos, registered };
      })
      .filter(({ registered }) => {
        if (!registered) return false;
        
        // 1. Permission Engine
        if (registered.config.permission && !can(registered.config.permission)) return false;
        
        // 2. Supported Roles (Presentation fallback/filter)
        if (registered.config.supportedRoles && user?.role) {
          if (!registered.config.supportedRoles.includes(user.role)) return false;
        }

        // 3. Feature Flags
        if (registered.config.featureFlag) {
          // Implement actual feature flag check here later if needed
          // if (!featureFlags.isEnabled(registered.config.featureFlag)) return false;
        }

        return true;
      });
  }, [layout.widgets, can, user?.role]);

  if (activeWidgets.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center rounded-xl border border-dashed border-outline bg-surface p-6 text-on-surface-variant">
        No widgets available for this layout or insufficient permissions.
      </div>
    );
  }

  return (
    <DashboardGrid>
      {activeWidgets.map(({ pos, registered }) => {
        if (!registered) return null; // Fallback, already filtered
        const Component = registered.component;
        
        return (
          <GridItem key={pos.id} w={pos.w} h={pos.h}>
            <Component id={pos.id} config={registered.config} />
          </GridItem>
        );
      })}
    </DashboardGrid>
  );
}
